import { useState, useCallback } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { PiWarningCircle } from 'react-icons/pi'
import { colors } from '@material-tailwind/react/types/generic'
import { useGeneralToast } from '../hooks/toast'
import { useWindowSize } from '../hooks/toolkit'
import { deleteAccessCode } from '../utils/helpers/supabase.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AccessCode } from '../types/supabase'

interface DeleteAccessCodeDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  codeToDelete: AccessCode
  refetchCodesList: () => void
}

const DeleteAccessCodeDialog: React.FC<DeleteAccessCodeDialogProps> = ({
  isOpen,
  setIsOpen,
  codeToDelete,
  refetchCodesList,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [deleting, setDeleting] = useState(false)
  const { width } = useWindowSize()
  const dialogSize = (width ?? 0) <= 640 ? 'lg' : 'xs'

  const handleDelete = useCallback(async () => {
    setDeleting(true)

    const { error } = await deleteAccessCode({
      accessCodeId: codeToDelete.id,
    })

    if (error) {
      toastError(t('agami.somethingWentWrong'))
      setDeleting(false)
      return
    }

    await refetchCodesList()
    toastSuccess('Access code successfully removed')
    setDeleting(false)
    setIsOpen(false)
  }, [deleteAccessCode, refetchCodesList, toastSuccess, toastError, t])

  return (
    <Dialog size={dialogSize} open={isOpen} handler={setIsOpen} data-testid="delete-access-code-dialog">
      <DialogHeader>{t('deleteDialog.title')}</DialogHeader>
      <DialogBody>
        <Alert
          variant={darkMode ? 'outlined' : 'ghost'}
          color="orange"
          icon={<PiWarningCircle className="h-6 w-6" />}
        >
          {t('deleteDialog.warning')}
        </Alert>
        <span className="block my-2 dark:text-base-gray-500">
          {t('deleteDialog.confirmMessage')} <strong>{codeToDelete?.code}</strong>?
        </span>
        <Alert variant={darkMode ? 'filled' : 'ghost'}>
          <ul className="mt-2 ml-2 list-outside list-disc">
            <li className="ml-2">{t('deleteDialog.effects.newUsers')}</li>
            <li className="ml-2">{t('deleteDialog.effects.existingUsers')}</li>
          </ul>
        </Alert>
      </DialogBody>
      <DialogFooter className="justify-between gap-2">
        <Button
          color={darkMode ? 'white' : ('primary' as colors)}
          variant="outlined"
          size="sm"
          onClick={() => setIsOpen(false)}
          disabled={deleting}
        >
          {t('deleteDialog.cancel')}
        </Button>
        <Button
          size="sm"
          color="red"
          onClick={handleDelete}
          disabled={deleting}
          data-testid="delete-access-code-confirm-btn"
        >
          {deleting ? <Spinner className="h-4 w-4" /> : t('deleteDialog.confirmDelete')}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default DeleteAccessCodeDialog
